import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import './VisionPage.css';

const visionParagraphs = [
  'Live content is growing faster than human attention can follow. Every day, important moments happen across sports, events, news, creators, communities, and live broadcasts, but most people still discover them too late, through fragmented clips, social feeds, or after the live experience has already passed.',
  'We believe live viewing is shifting from appointment viewing to moment viewing. HypeDirector is building the platform for that shift.',
  'Our vision is to become the live moment platform for the internet: the infrastructure that helps fans, publishers, broadcasters, stream owners, and partners detect, structure, distribute, and experience the moments that matter.',
  'The HypeDirector fan app is one important part of this ecosystem. It shows how audiences can discover, follow, and experience moments in a more direct and spoiler-free way. But the larger opportunity is the platform underneath: a B2B moment infrastructure that can support owners, broadcasters, publishers, sponsors, and third-party partners who need live moment intelligence inside their own products, workflows, and audiences.',
  'At the centre of HypeDirector is a human + AI moment engine designed to detect, structure, and route live moments with speed and accuracy. Human judgment remains essential where trust, context, and quality matter. AI expands what is possible by helping scale detection, improve consistency, and support more live experiences over time.',
  'Just as web crawlers made the internet searchable, HypeDirector\'s human + AI moment engine is designed to make live video discoverable in real time, moment by moment, spoiler-free.',
  'HypeDirector is more than an app. It is a scalable live moment platform built for B2C experiences, B2B infrastructure, and human + AI moment detection.',
];

const visionAudiences = [
  {
    title: 'For fans',
    body: 'Cut to the right moment at the right time without needing the result, score, or outcome revealed first.',
  },
  {
    title: 'For businesses',
    body: 'Increase engagement, retain audience attention, activate partners, build new products, and create value from live content.',
  },
  {
    title: 'For the live internet',
    body: 'Add a new layer of moment intelligence: not just where content is streamed, but where the important moment is happening.',
  },
];

export const VisionPage: React.FC = () => {
  const intl = useIntl();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="vision-page">
      <header className="vision-header">
        <Link to="/" className="vision-logo">
          <img src="/logo-transparent-no-slogan.png" alt={intl.formatMessage({ id: 'landing.brand.alt' })} />
        </Link>
        <Link to="/" className="vision-back-link">
          {intl.formatMessage({ id: 'landing.contact.backToHome' })}
        </Link>
      </header>

      <main className="vision-section">
        <article className="vision-container">
          <div className="vision-eyebrow">Our Vision</div>
          <h1>The Live Moment Platform</h1>
          <p className="vision-subtitle">
            HypeDirector is building the infrastructure layer that helps live moments get detected,
            structured, distributed, and experienced in real time.
          </p>

          <div className="vision-body">
            {visionParagraphs.slice(0, 5).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          <div className="vision-audience-grid">
            {visionAudiences.map((item) => (
              <section key={item.title} className="vision-audience-card">
                <h2>{item.title}</h2>
                <p>{item.body}</p>
              </section>
            ))}
          </div>

          <div className="vision-body vision-closing">
            {visionParagraphs.slice(5).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          <div className="vision-notice">
            <h2>Moment by moment, spoiler-free.</h2>
            <p>
              The fan app proves the experience. The platform underneath is the larger opportunity:
              live moment intelligence for audiences, owners, broadcasters, publishers, and partners.
            </p>
          </div>
        </article>
      </main>
    </div>
  );
};
